import * as React from "react";
import {
  UitkCard,
  UitkCardContent,
  UitkCardImageLayout,
  UitkCardLink,
  UitkCardMedia,
  UitkCardSectionSeparatorType,
  UitkCardTextSegment,
} from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { DestinationSelectorCardProps } from "../typings";
import { UitkPriceLockup } from "uitk-react-price-lockup";
import { UitkButton } from "uitk-react-button";
import { useLocalization } from "@shared-ui/localization-context";

export function DestinationSelectorCard(props: DestinationSelectorCardProps) {
  const description = props.destination.destinationDescriptions.MEDIUM;
  const isValidPrice = props.destination.averageLodgingCost !== "0";
  const cardOrdinal = (props.index + 1).toString();
  const imageLayout = props.isTablet ? UitkCardImageLayout.LEFT_FULLBLEED : undefined;
  const ratio = props.isTablet ? UitkFigureAspectRatioType.R16_9 : UitkFigureAspectRatioType.R21_9;
  const rfrr = `theme-selector.${props.affinity}.${props.destination.name}.click`;
  const informationButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
  const { formatText } = useLocalization();

  const onDialogShowClick = (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
    props.dialogShowOnClick(ev, informationButtonRef);
  };

  return (
    <div id={`destination-selector-card-${cardOrdinal}`}>
      <UitkCard withBorder={true} imageLayout={imageLayout}>
        <UitkCardMedia>
          <UitkFigure ratio={ratio}>
            <UitkImage src={props.destination.destinationImg} alt={props.destination.name} />
          </UitkFigure>
        </UitkCardMedia>
        <UitkCardContent>
          <UitkCardLink>
            <a
              aria-label={formatText("DestinationsByAffinity.getMoreDetailsLink", props.destination.name)}
              href={`${props.destination.url}&rfrr=${rfrr}`}
            />
          </UitkCardLink>
          <UitkCardTextSegment>
            <UitkLayoutFlex className="all-x-gutter-two" justifyContent="space-between" alignItems="start">
              <UitkLayoutFlexItem className="truncate">
                <div>
                  <h3 className="truncate uitk-type-heading-500">{props.destination.name}</h3>
                </div>
              </UitkLayoutFlexItem>
              {isValidPrice && (
                <UitkLayoutFlexItem>
                  <div>
                    <UitkLayoutFlex>
                      <UitkLayoutFlexItem shrink={1}>
                        <div>
                          <UitkButton
                            data-testid={`price-info-link-${cardOrdinal}`}
                            id={`price-info-link-${cardOrdinal}`}
                            domRef={informationButtonRef}
                            onClick={onDialogShowClick}
                            className="price-info-link-button"
                            icon="info_outline"
                            iconTitle={formatText("DestinationsByAffinity.priceIconTitle")}
                            iconId={`info-outline-${cardOrdinal}`}
                          />
                        </div>
                      </UitkLayoutFlexItem>
                      <UitkLayoutFlexItem shrink={0}>
                        <div>
                          <UitkPriceLockup
                            className="uitk-cell right-align all-x-gutter-zero"
                            lockupPrice={props.destination.averageLodgingCost}
                            priceSubtextFirst={formatText("DestinationsByAffinity.avgStatement")}
                            a11yPrice={formatText(
                              "DestinationByAffinity.price.a11y",
                              props.destination.averageLodgingCost
                            )}
                          />
                        </div>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </div>
                </UitkLayoutFlexItem>
              )}
              {!isValidPrice && (
                <UitkLayoutFlexItem className="get-rates uitk-type-heading-600">
                  <div>{formatText("hotels.price.getRates")}</div>
                </UitkLayoutFlexItem>
              )}
            </UitkLayoutFlex>
          </UitkCardTextSegment>
          <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP}>
            <p className="uitk-type-paragraph-300">{description}</p>
          </UitkCardTextSegment>
        </UitkCardContent>
      </UitkCard>
    </div>
  );
}
